import React, { FC, useRef } from "react";
import { Link } from "react-router-dom";
import { useApiQuery } from "../../../../../hooks/use-api-query";
import { Investment } from "../../../../../interfaces/investment";
import { InvitationBoard } from "../../../../../interfaces/invitation";
import { Opportunity } from "../../../../../interfaces/opportunity";
import { Alert } from "../../../../ui";
import Loading from "../../../../ui/loading";
import PermissionCheck from "../../../investor/permissionCheck";
import InvitationsCompletedCard from "./components/InvitationsCompletedCard";
import PendingOrNextReceiptCard from "./components/PendingOrNextReceiptCard";
import WalletCard from "./components/WalletCard";
import { OpenOpportunityCard } from "../../openOpportunityCard";
import { TotalCard } from "../../totalCard";
import { Profit } from "../../../../../interfaces/profit";
import AlertModal, { AlertModalRef } from "../../../../ui/alert/withModal";
import ProfitDecision from "./components/ProfitDecision";
import { OpenModalLink } from "../../../../ui/modal/Styled";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  ReactAccordionRoot,
} from "./Styles";
import SvgArrow from "../../../../ui/icons/arrow";

interface Wallet {
  total_invested: number;
  total_active: number;
  total_redempted: number;
  total_rewarded: number;
  total_received: number;
}

interface BoardData {
  id: string;
  wallet: Wallet;
}

interface DashboardResponse {
  board: BoardData;
  investments: Investment[];
  invitations: InvitationBoard;
  opportunities: Opportunity[];
  pending_profits: Profit[];
}

const AccountOverview: FC = () => {
  const { data, isLoading, refetch } = useApiQuery<DashboardResponse>([
    "investor",
    "dashboard",
  ]);

  const { board, investments, invitations, opportunities, pending_profits } =
    data || {};

  const profitModalsRef = useRef<{ [key: string]: AlertModalRef | null }>({});

  return (
    <section>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <div className="dashboard-cards">
            <PermissionCheck>
              {investments?.length === 0 && (
                <Alert
                  level="info"
                  message={
                    <span>
                      Você ainda não possui investimentos com a gente.{" "}
                      <Link
                        to="/investir"
                        className="g-color-mercatorio-gray-dark"
                        style={{ fontWeight: "bold" }}
                      >
                        Confira agora as oportunidades disponíveis!
                      </Link>
                    </span>
                  }
                />
              )}

              {pending_profits.length > 0 && (
                <ReactAccordionRoot type="single" collapsible>
                  <AccordionItem value="profits">
                    <AccordionTrigger>
                      Precatórios pagos! Defina o destino dos seus recursos
                      <SvgArrow />
                    </AccordionTrigger>

                    <AccordionContent>
                      {pending_profits.map((profit) => {
                        return (
                          <div key={profit.id}>
                            <AlertModal
                              ref={(el) =>
                                (profitModalsRef.current[profit.id] = el)
                              }
                              alertMessage={
                                <span>
                                  Um precatório da debênture{" "}
                                  <strong>{profit.investable.code}</strong> foi
                                  pago.{" "}
                                  <OpenModalLink
                                    onClick={() =>
                                      profitModalsRef.current[
                                        profit.id
                                      ]?.setShowModal(true)
                                    }
                                  >
                                    Clique aqui
                                  </OpenModalLink>{" "}
                                  para escolher entre resgatar ou reinvestir.
                                </span>
                              }
                              modalTitle="Termo de opção de reinvestimento/resgate de recursos"
                              modalContent={
                                <ProfitDecision
                                  profit={profit}
                                  onFinish={() => {
                                    refetch();
                                    profitModalsRef.current[
                                      profit.id
                                    ]?.setShowModal(false);
                                  }}
                                />
                              }
                              size="xl"
                              notDismissable
                              backdrop
                              keyboard
                            />
                          </div>
                        );
                      })}
                    </AccordionContent>
                  </AccordionItem>
                </ReactAccordionRoot>
              )}
            </PermissionCheck>

            <div className="dashboard-outer">
              <div className="dashboard-column">
                <div className="dashboard-wrapper">
                  {board && <TotalCard board={board} />}
                  <InvitationsCompletedCard invitations={invitations} />
                </div>

                {investments?.length > 0 && (
                  <PendingOrNextReceiptCard investments={investments} />
                )}
              </div>

              {investments && <WalletCard investments={investments} />}
            </div>

            {opportunities?.length > 0 && (
              <OpenOpportunityCard
                opportunity={opportunities[opportunities.length - 1]}
              />
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default AccountOverview;
